import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  FileText, 
  FileCheck, 
  Users, 
  Package,
  Search,
  ArrowRight,
  LayoutDashboard,
  BarChart3,
  Plus
} from 'lucide-react';

/**
 * CommandPalette — 2026 Search-First Command Modal
 * 
 * Inspired by Stripe × Linear × Notion
 * - Full modal with backdrop blur
 * - Search-first: navigation and discovery
 * - Create actions as secondary
 * - Keyboard-first (⌘K / Ctrl+K)
 * - Smooth fade + slide animation
 */
export default function CommandPalette({ 
  isOpen, 
  onClose, 
  onNavigate,
  currentPage
}) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const itemsRef = useRef([]);
  const modalRef = useRef(null);

  // Command definitions - Search-first approach
  const commands = [
    // Navigation (Primary)
    {
      id: 'nav-dashboard',
      label: 'Dashboard',
      description: 'Go to dashboard',
      icon: LayoutDashboard,
      action: () => onNavigate('dashboard'),
      keywords: ['dashboard', 'home', 'overview'],
      category: 'Navigate',
      priority: 10,
    },
    {
      id: 'nav-invoices',
      label: 'Invoices',
      description: 'View all invoices',
      icon: FileText,
      action: () => onNavigate('invoices'),
      keywords: ['invoices', 'bills', 'payments'],
      category: 'Navigate',
      priority: 9,
    },
    {
      id: 'nav-quotations',
      label: 'Quotations',
      description: 'View all quotations',
      icon: FileCheck,
      action: () => onNavigate('quotations'),
      keywords: ['quotations', 'quotes', 'estimates'],
      category: 'Navigate',
      priority: 9,
    },
    {
      id: 'nav-clients',
      label: 'Clients',
      description: 'View all clients',
      icon: Users,
      action: () => onNavigate('clients'),
      keywords: ['clients', 'customers'],
      category: 'Navigate',
      priority: 9,
    },
    {
      id: 'nav-products',
      label: 'Products',
      description: 'View all products',
      icon: Package,
      action: () => onNavigate('products'),
      keywords: ['products', 'services', 'items'],
      category: 'Navigate',
      priority: 9,
    },
    {
      id: 'nav-reports',
      label: 'Reports',
      description: 'View reports and analytics',
      icon: BarChart3,
      action: () => onNavigate('reports'),
      keywords: ['reports', 'analytics', 'stats'],
      category: 'Navigate',
      priority: 8,
    },
    
    // Create Actions (Secondary)
    {
      id: 'create-invoice',
      label: 'Create Invoice',
      description: 'Create a new invoice',
      icon: Plus,
      action: () => onNavigate('invoices', { action: 'create' }),
      keywords: ['create', 'new', 'invoice', 'bill'],
      category: 'Create',
      priority: currentPage === 'invoices' ? 7 : 5,
    },
    {
      id: 'create-quotation',
      label: 'Create Quotation',
      description: 'Create a new quotation',
      icon: Plus,
      action: () => onNavigate('quotations', { action: 'create' }),
      keywords: ['create', 'new', 'quotation', 'quote', 'estimate'],
      category: 'Create',
      priority: currentPage === 'quotations' ? 7 : 5,
    },
    {
      id: 'add-client',
      label: 'Add Client',
      description: 'Add a new client',
      icon: Plus,
      action: () => onNavigate('clients', { action: 'create' }),
      keywords: ['add', 'new', 'client', 'customer'],
      category: 'Create',
      priority: currentPage === 'clients' ? 7 : 5,
    },
    {
      id: 'add-product',
      label: 'Add Product',
      description: 'Add a new product or service',
      icon: Plus,
      action: () => onNavigate('products', { action: 'create' }),
      keywords: ['add', 'new', 'product', 'service', 'item'],
      category: 'Create',
      priority: currentPage === 'products' ? 7 : 5,
    },
  ];

  // Filter and group commands
  const filteredCommands = commands
    .filter(cmd => {
      if (!query) return true;
      const searchTerm = query.toLowerCase();
      return (
        cmd.label.toLowerCase().includes(searchTerm) ||
        cmd.description.toLowerCase().includes(searchTerm) ||
        cmd.keywords.some(kw => kw.includes(searchTerm))
      );
    })
    .sort((a, b) => b.priority - a.priority);

  // Group by category
  const groupedCommands = filteredCommands.reduce((acc, cmd) => {
    if (!acc[cmd.category]) {
      acc[cmd.category] = [];
    }
    acc[cmd.category].push(cmd);
    return acc;
  }, {});

  // Auto-focus input when opened
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredCommands.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredCommands[selectedIndex]) {
          executeCommand(filteredCommands[selectedIndex]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredCommands, selectedIndex, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (itemsRef.current[selectedIndex]) {
      itemsRef.current[selectedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selectedIndex]);

  // Execute command
  const executeCommand = useCallback((command) => {
    command.action();
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  // Render in portal at document root for global click-outside
  return createPortal(
    <>
      {/* Invisible click-outside layer - Global, covers entire viewport */}
      <div 
        className="fixed inset-0 z-40"
        onClick={onClose}
        role="button"
        tabIndex={-1}
        aria-label="Close command palette"
      />

      {/* Command Palette - Morphs from search bar */}
      <div 
        className="fixed z-50"
        style={{
          top: '8px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '640px',
          maxWidth: 'calc(100vw - 32px)',
        }}
      >
        {/* Pointer/Notch - Indicates origin from search bar */}
        <div 
          className="absolute -top-2 left-1/2 -translate-x-1/2 z-10"
          style={{
            width: '16px',
            height: '8px',
            animation: 'pointerFade 350ms cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <svg width="16" height="8" viewBox="0 0 16 8" fill="none">
            <path 
              d="M0 8 L8 0 L16 8" 
              fill="white"
              style={{
                filter: 'drop-shadow(0 -2px 4px rgba(15, 23, 42, 0.08))',
              }}
            />
          </svg>
        </div>

        {/* Palette Container - Morphs from search input */}
        <div 
          ref={modalRef}
          className="bg-white rounded-[14px] overflow-hidden"
          style={{
            boxShadow: '0 0 0 0.5px rgba(15, 23, 42, 0.08), 0 20px 60px rgba(15, 23, 42, 0.14), 0 8px 24px rgba(15, 23, 42, 0.10)',
            animation: 'paletteMorph 350ms cubic-bezier(0.16, 1, 0.3, 1)',
            transformOrigin: 'top center',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search Input - Strong focus ring, Apple-style */}
          <div className="relative border-b border-slate-100/80">
            <Search 
              size={18} 
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelectedIndex(0);
              }}
              placeholder="Search or jump to..."
              className="
                w-full h-[54px] pl-12 pr-4 text-[15px]
                bg-transparent
                outline-none
                placeholder:text-slate-400
                text-slate-900
                focus:ring-0
              "
              style={{
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", system-ui, sans-serif',
                fontWeight: 400,
                letterSpacing: '-0.01em',
              }}
            />
          </div>

          {/* Commands List - Subtle hover, no harsh dividers */}
          <div className="max-h-[400px] overflow-y-auto">
            {filteredCommands.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <p className="text-[13px] text-slate-500">
                  No results found for "{query}"
                </p>
              </div>
            ) : (
              <div className="py-1.5">
                {Object.entries(groupedCommands).map(([category, cmds]) => (
                  <div key={category} className="mb-1 last:mb-0">
                    {/* Category Header - Subtle */}
                    <div className="px-4 py-1.5">
                      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                        {category}
                      </p>
                    </div>

                    {/* Commands in Category */}
                    {cmds.map((command, index) => {
                      const globalIndex = filteredCommands.indexOf(command);
                      const Icon = command.icon;
                      const isSelected = globalIndex === selectedIndex;

                      return (
                        <button
                          key={command.id}
                          ref={el => itemsRef.current[globalIndex] = el}
                          onClick={() => executeCommand(command)}
                          onMouseEnter={() => setSelectedIndex(globalIndex)}
                          className={`
                            w-full px-3 py-2.5 mx-1
                            flex items-center gap-3
                            text-left
                            rounded-lg
                            transition-all duration-100
                            ${isSelected 
                              ? 'bg-slate-100/80' 
                              : 'hover:bg-slate-50/50'
                            }
                          `}
                          style={{
                            width: 'calc(100% - 8px)',
                          }}
                        >
                          {/* Icon - Low contrast by default, brighten on focus */}
                          <div 
                            className={`
                              w-8 h-8 rounded-[8px]
                              flex items-center justify-center
                              transition-all duration-150
                              ${isSelected
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : 'bg-slate-100/60 text-slate-500'
                              }
                            `}
                          >
                            <Icon size={16} strokeWidth={2} />
                          </div>

                          {/* Label & Description */}
                          <div className="flex-1 min-w-0">
                            <div 
                              className="text-[13px] font-medium text-slate-900"
                              style={{
                                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
                                letterSpacing: '-0.01em',
                              }}
                            >
                              {command.label}
                            </div>
                            <div 
                              className="text-[11px] text-slate-500 truncate"
                              style={{
                                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif',
                              }}
                            >
                              {command.description}
                            </div>
                          </div>

                          {/* Arrow Hint - Subtle */}
                          {isSelected && (
                            <div className="flex items-center gap-1 text-slate-400">
                              <ArrowRight size={14} strokeWidth={2} />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer - Keyboard Hints, Apple-style */}
          <div className="border-t border-slate-100/60 px-4 py-2.5 bg-slate-50/30">
            <div className="flex items-center justify-between text-[11px] text-slate-500">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-white/80 border border-slate-200/60 rounded-md text-[10px] font-mono shadow-sm">↑</kbd>
                  <kbd className="px-1.5 py-0.5 bg-white/80 border border-slate-200/60 rounded-md text-[10px] font-mono shadow-sm">↓</kbd>
                  <span className="ml-1">Navigate</span>
                </div>
                <div className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-white/80 border border-slate-200/60 rounded-md text-[10px] font-mono shadow-sm">↵</kbd>
                  <span className="ml-1">Select</span>
                </div>
                <div className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-white/80 border border-slate-200/60 rounded-md text-[10px] font-mono shadow-sm">Esc</kbd>
                  <span className="ml-1">Close</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Animations - Smooth, elegant morphing */}
      <style>{`
        @keyframes pointerFade {
          0% {
            opacity: 0;
            transform: translateY(-8px);
          }
          60% {
            opacity: 0.6;
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes paletteMorph {
          0% {
            opacity: 0;
            transform: scaleY(0.25);
            border-radius: 10px;
          }
          50% {
            opacity: 0.8;
          }
          100% {
            opacity: 1;
            transform: scaleY(1);
            border-radius: 14px;
          }
        }
        
        /* Smooth scrollbar - Apple-style */
        .max-h-\\[400px\\]::-webkit-scrollbar {
          width: 8px;
        }
        
        .max-h-\\[400px\\]::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .max-h-\\[400px\\]::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.3);
          border-radius: 4px;
        }
        
        .max-h-\\[400px\\]::-webkit-scrollbar-thumb:hover {
          background: rgba(148, 163, 184, 0.5);
        }
      `}</style>
    </>,
    document.body
  );
}
